import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search, Grid, List, MoreVertical, Trash2, Copy, Loader2 } from 'lucide-react'
import clsx from 'clsx'
import { useAuthStore } from '@/stores/authStore'
import { coversApi, Cover } from '@/services/api'

const API_BASE = import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost' ? '' : 'https://aistudio-api.wizice100.workers.dev')

// 프로젝트에서 표시할 이미지 추출
const getProjectImage = (project: Cover): string | null => {
  // thumbnailUrl이 백엔드에서 이미 처리됨 (레이어 이미지 또는 실제 썸네일)
  if (project.thumbnailUrl) {
    // 상대 경로면 API_BASE 추가
    if (project.thumbnailUrl.startsWith('/')) {
      return `${API_BASE}${project.thumbnailUrl}`
    }
    return project.thumbnailUrl
  }
  return null
}

export default function ProjectsPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [searchQuery, setSearchQuery] = useState('')
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [projects, setProjects] = useState<Cover[]>([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState<'updated' | 'created' | 'name'>('updated')

  // 로그인 필수 - 비로그인시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // 프로젝트 목록 조회
  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true)
      try {
        const result = await coversApi.list({ limit: 100 })
        if (result.success && result.data) {
          setProjects(result.data)
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err)
      } finally {
        setLoading(false)
      }
    }
    if (isAuthenticated) {
      fetchProjects()
    }
  }, [isAuthenticated])

  // 프로젝트 삭제
  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`"${title}" 프로젝트를 삭제하시겠습니까?`)) return

    try {
      const result = await coversApi.delete(id)
      if (result.success) {
        setProjects(projects.filter(p => p.id !== id))
        setActiveMenu(null)
        alert('삭제되었습니다.')
      } else {
        const errorMsg = result.error?.message || '알 수 없는 오류'
        console.error('Delete failed:', result.error)
        alert(`삭제 실패: ${errorMsg}`)
      }
    } catch (err) {
      console.error('Failed to delete project:', err)
      alert('네트워크 오류가 발생했습니다.')
    }
  }

  // 프로젝트 복제
  const handleDuplicate = async (id: string) => {
    try {
      const result = await coversApi.duplicate(id)
      if (result.success && result.data) {
        setProjects([result.data, ...projects])
        setActiveMenu(null)
      } else {
        alert('복제 중 오류가 발생했습니다.')
      }
    } catch (err) {
      console.error('Failed to duplicate project:', err)
      alert('복제 중 오류가 발생했습니다.')
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // 검색 및 정렬
  const filteredProjects = projects
    .filter(p => p.title.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'updated') {
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      } else if (sortBy === 'created') {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      } else {
        return a.title.localeCompare(b.title)
      }
    })

  return (
    <div className="min-h-[calc(100vh-56px)] p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold">내 프로젝트</h1>

          <Link to="/editor" className="btn btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            새 표지 만들기
          </Link>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
            <input
              type="text"
              placeholder="프로젝트 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
          </div>

          <div className="flex items-center gap-2">
            <select
              className="input w-auto"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'updated' | 'created' | 'name')}
            >
              <option value="updated">최근 수정순</option>
              <option value="created">생성일순</option>
              <option value="name">이름순</option>
            </select>

            <div className="flex border border-border-default rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={clsx(
                  'p-2 transition-colors',
                  viewMode === 'grid' ? 'bg-bg-elevated text-text-primary' : 'text-text-tertiary hover:text-text-secondary'
                )}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={clsx(
                  'p-2 transition-colors',
                  viewMode === 'list' ? 'bg-bg-elevated text-text-primary' : 'text-text-tertiary hover:text-text-secondary'
                )}
              >
                <List className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
          </div>
        )}

        {/* Projects Grid/List */}
        {!loading && viewMode === 'grid' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {/* New Project Card */}
            <Link
              to="/editor"
              className="card border-dashed border-2 border-border-default hover:border-accent-gold transition-colors flex flex-col items-center justify-center aspect-cover"
            >
              <Plus className="w-8 h-8 text-text-tertiary mb-2" />
              <span className="text-text-secondary text-sm">새 표지</span>
            </Link>

            {filteredProjects.map((project) => {
              const imageUrl = getProjectImage(project)
              return (
              <div key={project.id} className="card-elevated group relative">
                <Link to={`/editor/${project.id}`} className="block">
                  <div className="aspect-cover bg-bg-elevated flex items-center justify-center text-text-tertiary overflow-hidden">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={project.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // 이미지 로드 실패시 기본 플레이스홀더 표시
                          e.currentTarget.style.display = 'none'
                          e.currentTarget.parentElement!.innerHTML = `<span class="text-xs">${project.width} x ${project.height}</span>`
                        }}
                      />
                    ) : (
                      <span className="text-xs">
                        {project.width} x {project.height}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium truncate">{project.title}</h3>
                    <p className="text-xs text-text-tertiary mt-1">
                      수정: {formatDate(project.updated_at)}
                    </p>
                  </div>
                </Link>

                {/* Menu Button */}
                <div className="absolute top-2 right-2">
                  <button
                    onClick={() => setActiveMenu(activeMenu === project.id ? null : project.id)}
                    className="p-1.5 bg-bg-primary/80 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {activeMenu === project.id && (
                    <div className="absolute top-full right-0 mt-1 w-40 bg-bg-tertiary border border-border-subtle rounded-lg shadow-lg py-1 z-10">
                      <button
                        onClick={() => handleDuplicate(project.id)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-bg-elevated transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                        복제
                      </button>
                      <button
                        onClick={() => handleDelete(project.id, project.title)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-accent-error hover:bg-bg-elevated transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        삭제
                      </button>
                    </div>
                  )}
                </div>
              </div>
              )
            })}
          </div>
        )}

        {!loading && viewMode === 'list' && (
          <div className="space-y-2">
            {filteredProjects.map((project) => {
              const imageUrl = getProjectImage(project)
              return (
              <Link
                key={project.id}
                to={`/editor/${project.id}`}
                className="card hover:bg-bg-elevated flex items-center gap-4 p-4 transition-colors"
              >
                <div className="w-16 h-20 bg-bg-elevated rounded flex items-center justify-center text-text-tertiary flex-shrink-0 overflow-hidden">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={project.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        e.currentTarget.parentElement!.innerHTML = `<span class="text-xs">${project.width}x${project.height}</span>`
                      }}
                    />
                  ) : (
                    <span className="text-xs">{project.width}x{project.height}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{project.title}</h3>
                  <p className="text-sm text-text-tertiary">
                    {project.width} x {project.height}
                  </p>
                </div>
                <div className="text-right text-sm text-text-tertiary">
                  <div>수정: {formatDate(project.updated_at)}</div>
                  <div>생성: {formatDate(project.created_at)}</div>
                </div>
              </Link>
              )
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredProjects.length === 0 && !searchQuery && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-bg-elevated flex items-center justify-center">
              <Plus className="w-8 h-8 text-text-tertiary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">아직 프로젝트가 없습니다</h2>
            <p className="text-text-secondary mb-6">
              새 표지를 만들어 시작해보세요!
            </p>
            <Link to="/editor" className="btn btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              새 표지 만들기
            </Link>
          </div>
        )}

        {/* No Search Results */}
        {!loading && filteredProjects.length === 0 && searchQuery && (
          <div className="text-center py-16">
            <Search className="w-12 h-12 mx-auto mb-4 text-text-tertiary" />
            <h2 className="text-xl font-semibold mb-2">검색 결과가 없습니다</h2>
            <p className="text-text-secondary">
              다른 검색어를 시도해보세요.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
